import { type FC, useMemo } from "react";
import ModalContainer from "~/common/components/ModalContainer";
import { useDispatch, useSelector } from "react-redux";
import { restorePreviousSnapshot } from "~/features/configurator/Linear/redux/actions";
import { Button, Stack, styled } from "~/common/mui";
import { getConfiguratorType, getProductCode } from "~/features/configurator/Linear/redux/selectors";
import { Card } from "~/features/configurator/Linear/components/Card";
import { resolveApiUrl } from "~/utils/url/urlResolver";
import { ConfiguratorType } from "~/domains/configurator/types/ConfiguratorType";
import {
  RESET_URL_CARRIAGES_PREFIX,
  RESET_URL_LINEAR_PREFIX,
  RESET_URL_RAIL_PREFIX,
  RESET_URL_SHAFT_PREFIX,
} from "~/features/configurator/Linear/constants";
import { useTranslation } from "react-i18next";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WarningIcon from "@mui/icons-material/Warning";

export const ConflictPopupCard = styled(Card)(({ theme }) => ({
  background: theme.palette.common.white,
  display: "flex",
  flexDirection: "column",
  padding: "30px 30px 0",
}));

export const ConflictList = styled("ul")(({ theme }) => ({
  marginBottom: theme.spacing(3.75),
  paddingLeft: 0,
}));

export const ConflictListItem = styled("li")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(0.625),
}));

function getResetPath(configuratorType: ConfiguratorType | undefined) {
  if (configuratorType === ConfiguratorType.Carriage) {
    return RESET_URL_CARRIAGES_PREFIX;
  } else if (configuratorType === ConfiguratorType.Rail) {
    return RESET_URL_RAIL_PREFIX;
  } else if (configuratorType === ConfiguratorType.MetricShaft || configuratorType === ConfiguratorType.InchShaft) {
    return RESET_URL_SHAFT_PREFIX;
  } else {
    return RESET_URL_LINEAR_PREFIX;
  }
}

const InConflictModal: FC = () => {
  const dispatch = useDispatch();
  const configuratorType = useSelector(getConfiguratorType);
  const resetPath = getResetPath(configuratorType);
  const resetUrl =
    resetPath === RESET_URL_SHAFT_PREFIX
      ? resolveApiUrl(`/shaft/${useSelector(getProductCode)}/${resetPath}/reset`)
      : resolveApiUrl(`/${useSelector(getProductCode)}/${resetPath}/reset`);
  const { t } = useTranslation();

  const { onStepBack, onReset } = useMemo(
    () => ({
      onStepBack: () => dispatch(restorePreviousSnapshot()),
      onReset: () => window.location.assign(resetUrl),
    }),
    [],
  );

  return (
    <ModalContainer>
      <div className="scha__fullscreen-loader d-flex flex-justify-center">
        <ConflictPopupCard disableHover={true}>
          <p className={"mt-0 mb-3 title-medium"}>{t("page.linear.conflict.message")}</p>

          <ConflictList>
            <ConflictListItem>
              <ArrowBackIcon className={"icon-margin f-green"} sx={{ fontSize: 20 }} />
              <span>{t("page.linear.conflict.previous.label")}</span>
            </ConflictListItem>
            <ConflictListItem>
              <WarningIcon className={"icon-margin f-error-red"} sx={{ fontSize: 20 }} />
              <span>{t("page.linear.conflict.reset.label")}</span>
            </ConflictListItem>
          </ConflictList>
          <div className={"scha__modal__footer"}>
            <Stack className={"d-flex flex-justify-end"} gap={4}>
              <Button
                color={"secondary"}
                startIcon={<WarningIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />}
                onClick={onReset}
                data-test-id={"conflict-modal-reset"}
              >
                {t("page.linear.conflict.reset.button")}
              </Button>
              <Button
                startIcon={<ArrowBackIcon sx={{ fontSize: { phone: 16, tablet: 20 } }} />}
                onClick={onStepBack}
                data-test-id={"conflict-modal-step-back"}
              >
                {t("page.linear.conflict.previous.button")}
              </Button>
            </Stack>
          </div>
        </ConflictPopupCard>
      </div>
    </ModalContainer>
  );
};
export default InConflictModal;
